import axios from "axios";
import { API_BASE } from "../config.js";
import { getAccessToken } from "../auth/apiAuth.jsx";

const api = axios.create({
  baseURL: `${API_BASE}/api/data`,
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  const tk = getAccessToken && getAccessToken();
  if (tk) config.headers.Authorization = `Bearer ${tk}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error.response) {
      const { status, data } = error.response;
      let msg =
        data?.error?.message ||
        (typeof data?.error === "string" ? data.error : null) ||
        data?.detail ||
        data?.message ||
        "Unknown error";
      const e = new Error(msg);
      e.status = status;
      e.details = data;
      if (status === 401) e.message = "Your session has expired. Please sign in again.";
      return Promise.reject(e);
    }
    return Promise.reject(error);
  }
);

/**
 * Get list of vendors with optional filtering
 */
export async function getVendors({ search, category, status, risk, approval, tier, company, page, pageSize } = {}) {
  const params = {};
  if (search) params.search = search;
  if (category) params.category = category;
  if (status) params.status = status;
  if (risk) params.risk = risk;
  if (approval) params.approval = approval;
  if (tier) params.tier = tier;
  if (company) params.company = company;
  if (page) params.page = page;
  if (pageSize) params.pageSize = pageSize;

  const response = await api.get("/vendors", { params });
  return response.data;
}

/**
 * Get vendor by vendor number
 */
export async function getVendorById(vendorNo) {
  const response = await api.get(`/vendors/${encodeURIComponent(vendorNo)}`);
  return response.data;
}

/**
 * Create a new vendor
 */
export async function createVendor(vendorData) {
  const response = await api.post("/vendors", vendorData);
  return response.data;
}

/**
 * Update vendor extension fields
 */
export async function updateVendor(vendorNo, updateData) {
  const response = await api.patch(`/vendors/${encodeURIComponent(vendorNo)}`, updateData);
  return response.data;
}

/**
 * Get vendor ledger entries (purchase invoices)
 */
export async function getVendorLedgerEntries(vendorNo, { filter, top } = {}) {
  const params = {};
  if (top) params.top = top;
  const response = await api.get(`/vendors/${encodeURIComponent(vendorNo)}/ledger`, { params });
  return response.data;
}

export async function getDetailedVendorLedgerEntries(vendorNo, options = {}) {
  return getVendorLedgerEntries(vendorNo, options);
}
